import { parseUTCToLocalTimezone } from '../../../i18n/LocalizedDateDistance';
import i18n from '../../../i18n';

/**
 * Calculate deadline progress percentage and determine color based on completion
 * @param {Object} options - Configuration object
 * @param {Date|string} options.scheduledDate - When work should start
 * @param {Date|string} options.dueDate - When task is due
 * @param {Date|string} [options.completionDate] - When task was completed (optional)
 * @returns {Object} Progress information { percent: number, color: string }
 *
 * Progress starts at 0% on scheduledDate and reaches 100% at dueDate.
 * If completionDate exists, progress calculation is frozen at that date.
 * Color is determined by progress: success (0-74%), warning (75-99%), error (100%+)
 */
export function calculateDeadlineProgress({
  scheduledDate,
  dueDate,
  completionDate,
}) {
  const language = i18n.language || 'en';

  // Parse dates with timezone compensation for completion date
  const due = dueDate ? new Date(dueDate) : null;
  const scheduled = scheduledDate ? new Date(scheduledDate) : null;
  const completed = completionDate ? parseUTCToLocalTimezone(completionDate, language) : null;

  if (!due) {
    return { percent: 0, color: 'success' };
  }

  // Normalize dates to midnight for consistent day-based calculations
  due.setHours(23, 59, 0, 0);
  if (scheduled) {
    scheduled.setHours(0, 0, 0, 0);
  }

  // Use completion date if available, otherwise use current date
  const now = completed || new Date();

  // Calculate progress percentage
  const startDate = scheduled || due;
  const totalTime = due.getTime() - startDate.getTime();
  const elapsedTime = Math.min(now.getTime(), due.getTime()) - startDate.getTime();
  const progressPercent = Math.min(100, Math.max(0, (elapsedTime / totalTime) * 100));

  // Determine color based on progress
  const getColor = () => {
    if (progressPercent >= 100) return 'error';
    if (progressPercent >= 75) return 'warning';
    return 'success';
  };

  const color = getColor();
  return {
    percent: progressPercent,
    color,
    // Fill for progress bars: the brighter `light` shade. `main` is dark enough
    // for white text, which on a thin bar reads as brown (warning) or maroon (error).
    barColor: `${color}.light`,
  };
}

/**
 * Calculate positions for day marker dots on the progress bar
 * @param {Object} options - Configuration object
 * @param {Date|string} options.scheduledDate - When work should start
 * @param {Date|string} options.dueDate - When task is due
 * @returns {Array} Array of day marker positions { dayNumber: number, percent: number }
 *
 * Creates day markers at equal intervals from scheduled date to due date.
 * If a task has 3 days total, markers are placed at day 1 and day 2 (not at the boundaries).
 */
export function calculateDayMarkers({ scheduledDate, dueDate }) {
  const due = dueDate ? new Date(dueDate) : null;
  const scheduled = scheduledDate ? new Date(scheduledDate) : null;

  if (!due || !scheduled) {
    return [];
  }

  // Normalize dates to midnight for consistent day-based calculations
  due.setHours(23, 59, 0, 0);
  scheduled.setHours(0, 0, 0, 0);

  const startDate = scheduled;
  const totalMs = due.getTime() - startDate.getTime();
  const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));

  if (totalDays <= 1) {
    return [];
  }

  const markers = [];
  // Create markers for each day boundary (except start and end)
  for (let day = 1; day < totalDays; day++) {
    const percent = (day / totalDays) * 100;
    markers.push({ dayNumber: day, percent });
  }

  return markers;
}
